import React, { useCallback, useMemo, useState } from 'react';
import { App, Button, Dropdown, Empty, List, Tag, Typography } from 'antd';

import TabLoadState from '../common/TabLoadState';

import RecordBadge from '../common/RecordBadge';
import { LAUNCH_SCOPE_TITLES } from '../common/LaunchButtonRow';
import { buildActionMenu } from '../common/actionMenu';
import { buildMemberCardOptions } from '../cards/CardVisuals';
import { COLLECTION_ICON, KEBAB, PERSON, PLUS } from '../icons';
import { QUICK_ADD_SCOPE } from '../../hooks/useQuickAddTemplates';
import { getCollectionMemberCardIds } from '../../utils/collections';
import { canCreateItems, canManage, canWriteCard } from '../../utils/permissions';
import { normalizeId } from '../../utils/ids';
import { isDuplicateProfile } from '../../utils/duplicateProfiles';
import QuickAddTemplateModal from './QuickAddTemplateModal';
import { selectQuickAddTemplates } from './QuickAddButtons';
import { PIN_KIND } from '../../services/favorites';
import { useOptionalFavorites } from '../../contexts/FavoritesContext';
import { confirmDestructive } from '../common/confirmDestructive';

/**
 * The management list of a collection's quick add templates — shared by the
 * Quick add settings tab and the Manage… modal off the Add item picker. Each
 * row reads in the two tiers the multi add list uses: the template's CARD as
 * its badge, the template's name and the tier glyph (the same words the
 * launch row uses) on top; the card it adds to beneath them, muted. The ⋯
 * menu keeps its square at the right, in the platform taxonomy: Edit… · Pin
 * for everyone / Unpin (managers) · Delete….
 *
 * The API's 404-vs-403 line is honoured by simply not offering what the
 * caller's role lacks: only their OWN user-tier templates are ever listed, so
 * a user-tier row is always editable; a collection-tier row is editable by
 * managers only. The server re-checks everything.
 *
 * A DUPLICATE PROFILE (a template that copies from an existing item) lists
 * here with the rest — it is a quick add template — under a "Duplicate
 * profile" tag, plus "Default for Duplicate" on the card's default. It is
 * launched from an item's Duplicate ▾, never from the Add item picker.
 *
 * `store` is the useQuickAddTemplates result of the surface that owns the
 * list (one list, one source of truth — the Add item modal's launch row and
 * this manager share it).
 */

const SCOPE_GLYPHS = { collection: COLLECTION_ICON, user: PERSON };

function ScopeGlyph({ scope }) {
	const Glyph = SCOPE_GLYPHS[scope];
	if (!Glyph) return null;
	return (
		<span
			className={`quick-add-list__scope quick-add-list__scope--${scope}`}
			title={LAUNCH_SCOPE_TITLES[scope]}
			role="img"
			aria-label={scope === QUICK_ADD_SCOPE.COLLECTION ? 'Pinned for everyone' : 'Yours'}
		>
			<Glyph />
		</span>
	);
}

export default function QuickAddList({ collectionId, collection, cardsById, store }) {
	const { modal } = App.useApp();
	const { templates = [], loading, loadError, reload, remove, promote, demote } = store || {};
	const isManager = canManage(collection);
	const canCreate = canCreateItems(collection);
	const favorites = useOptionalFavorites();
	// A template can go on Home only when this person can launch it (write
	// on its card) — the API says the same with a 403.
	const launchableIds = useMemo(
		() => new Set(selectQuickAddTemplates({ collection, templates, cardsById }).map(entry => normalizeId(entry.template?._id))),
		[collection, templates, cardsById]
	);
	// { mode: 'create' } | { mode: 'edit', template, card } — null when closed.
	const [editing, setEditing] = useState(null);

	const cards = useMemo(() => Object.values(cardsById || {}), [cardsById]);

	// The cards a NEW template may be bound to: writable members, in the
	// hierarchy order the Add item picker uses.
	const cardOptions = useMemo(() => {
		if (!collection) return [];
		const memberIds = getCollectionMemberCardIds(collection, cards);
		return buildMemberCardOptions(cards, memberIds).filter(opt => canWriteCard(collection, opt.value));
	}, [collection, cards]);

	const confirmDelete = useCallback((template) => {
		confirmDestructive(modal, {
			title: `Delete "${template?.name}"?`,
			content: template?.scope === QUICK_ADD_SCOPE.COLLECTION
				? 'It disappears for everyone who uses this collection. History can restore it.'
				: 'Only you can see this quick add, so only you lose it. History can restore it.',
			okText: 'Delete',
			onOk: () => remove(template),
		});
	}, [modal, remove]);

	const rows = useMemo(() => templates.map(template => {
		const cardId = normalizeId(template?.card_id);
		const card = cardId ? cardsById?.[cardId] || null : null;
		const isCollectionTier = template?.scope === QUICK_ADD_SCOPE.COLLECTION;
		return {
			id: normalizeId(template?._id),
			template,
			card,
			scope: isCollectionTier ? QUICK_ADD_SCOPE.COLLECTION : QUICK_ADD_SCOPE.USER,
			// A collection-tier template is editable by managers only; a
			// user-tier one is always the caller's own.
			mayEdit: !isCollectionTier || isManager,
		};
	}), [templates, cardsById, isManager]);

	const menuFor = (row) => buildActionMenu([
		{
			key: 'edit',
			group: 'record',
			glyph: 'edit',
			label: 'Edit…',
			hidden: !row.mayEdit,
			// A template whose card the cache lacks has nothing to edit
			// against; the row still lists (and deletes).
			disabled: !row.card,
			onClick: () => setEditing({ mode: 'edit', template: row.template, card: row.card }),
		},
		{
			key: 'pin-home',
			group: 'record',
			glyph: 'pin',
			label: favorites?.isPinned?.(PIN_KIND.QUICK_ADD, row.id, collectionId) ? 'Unpin from Home' : 'Pin to Home',
			hidden: !favorites?.togglePin || !launchableIds.has(row.id),
			onClick: () => favorites.togglePin(PIN_KIND.QUICK_ADD, { targetId: row.id, collectionId: normalizeId(collectionId) }),
		},
		{
			key: 'promote',
			group: 'record',
			glyph: 'collection',
			label: 'Pin for everyone',
			hidden: !isManager || row.scope === QUICK_ADD_SCOPE.COLLECTION,
			onClick: () => promote(row.template),
		},
		{
			key: 'demote',
			group: 'record',
			glyph: 'collection',
			label: 'Unpin — return to your own list',
			hidden: !isManager || row.scope !== QUICK_ADD_SCOPE.COLLECTION,
			onClick: () => demote(row.template),
		},
		{
			key: 'delete',
			group: 'destructive',
			glyph: 'delete',
			label: 'Delete…',
			hidden: !row.mayEdit,
			onClick: () => confirmDelete(row.template),
		},
	]);

	// Until a first read has answered, a skeleton: never "No quick adds yet" ahead of it,
	// and a failed read is an error with Retry, not an empty list (§14).
	if (!templates.length && (loading || loadError)) {
		return <TabLoadState loading={loading} error={!loading && loadError} label='quick adds' onRetry={reload} rows={3} />;
	}

	return (
		<div className="quick-add-list">
			{canCreate && cardOptions.length ? (
				<div className="quick-add-list__actions">
					<Button type="primary" icon={<PLUS />} onClick={() => setEditing({ mode: 'create' })}>
						New quick add…
					</Button>
				</div>
			) : null}

			<List
				bordered
				dataSource={rows}
				rowKey={row => row.id}
				locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No quick adds yet" /> }}
				renderItem={row => {
					const menu = menuFor(row);
					const name = row.template?.name || '(untitled quick add)';
					const cardLine = row.card ? row.card.name : '(card unavailable)';
					return (
						<List.Item key={row.id} data-scope={row.scope}>
							<div className="quick-add-list__row">
								<div className="quick-add-list__main">
									<div className="quick-add-list__lead">
										<RecordBadge
											kind="card"
											record={row.card || { name: '?' }}
											variant="plain"
											size="sm"
											title={name}
										/>
										<ScopeGlyph scope={row.scope} />
									</div>
									<Typography.Text type="secondary" className="quick-add-list__card" title={cardLine}>
										{cardLine}
									</Typography.Text>
									{isDuplicateProfile(row.template) ? (
										<div className="quick-add-list__tags">
											<Tag bordered={false} data-testid="duplicate-profile-tag">Duplicate profile</Tag>
											{row.template?.defaultForDuplicate ? (
												<Tag bordered={false} data-testid="default-duplicate-tag">Default for Duplicate</Tag>
											) : null}
										</div>
									) : null}
								</div>
								{menu ? (
									<Dropdown trigger={['click']} placement="bottomRight" menu={menu}>
										<Button icon={<KEBAB />} aria-label={`Actions for ${name}`} />
									</Dropdown>
								) : null}
							</div>
						</List.Item>
					);
				}}
			/>

			<QuickAddTemplateModal
				open={!!editing}
				mode={editing?.mode || 'create'}
				template={editing?.template || null}
				card={editing?.card || null}
				collectionId={collectionId}
				collection={collection}
				cardOptions={cardOptions}
				store={store}
				onCancel={() => setEditing(null)}
				onSaved={() => setEditing(null)}
			/>
		</div>
	);
}
